import { createElement as rc } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { globalConfig as getGlobalConfig, uiRuntimeConfig } from 'lib_ui-services';
import { hooks } from 'lib_ui-primitives';

export const _private = {
    getGlobalConfig,
    uiRuntimeConfig,
    useRouter: hooks.useRouter
};

/**
 * Creates the browser Auth0 provider from runtime configuration for the current
 * brand and logical Environment. A configured audience is used when the UI is
 * served from a concrete candidate Deployment Target but tokens must target the
 * Environment's public API. Existing deployments without that value retain the
 * browser-origin `/api` audience. Missing client identity fails before the Auth0
 * SDK is created so bootstrap reports the configuration problem directly.
 */
const Auth0ProviderWithHistory = ({ children }) => {
    const { brand, environment } = _private.getGlobalConfig();
    const auth0 = _private.uiRuntimeConfig.getAuth0Config({ brand, environment });
    const domain = auth0.AUTH0_DOMAIN;
    const clientId = auth0.AUTH0_CLIENT_ID;

    // Missing runtime Auth0 config should fail before we hand undefined values
    // to the Auth0 SDK, where the resulting error is harder to connect to env
    // or runtime-global configuration.
    if (!domain || !clientId) {
        throw new Error(
            `Missing Auth0 runtime configuration: AUTH0_DOMAIN and AUTH0_CLIENT_ID must be set for brand "${brand}" in environment "${environment}".`
        );
    }

    const { protocol, host, groupNumber, history } = _private.useRouter();
    const groupPath = groupNumber ? `/g/${groupNumber}` : '';

    const onRedirectCallback = appState => {
        if (appState?.returnTo) {
            history.push(appState?.returnTo);
        } else {
            history.push(`${groupPath}/`);
        }
    };

    const redirectUri = `${protocol}//${host}${groupPath}/`;
    // Candidate Target hostnames are valid redirect locations, but their API
    // hostname is not necessarily the Auth0 audience for the logical Environment.
    return rc(
        Auth0Provider,
        {
            domain,
            clientId,
            redirectUri,
            audience: auth0.AUTH0_AUDIENCE || `${protocol}//${host}/api`,
            onRedirectCallback
        },
        children
    );
};

export default Auth0ProviderWithHistory;
